import { clubApi } from '@repo/api-client';
import { Circle, CircleCheck } from 'lucide-react';
import { useMemo } from 'react';
import { useRevalidator } from 'react-router';

const TAGS_COLORS = [
  'bg-purple-100 text-purple-800',
  'bg-red-100 text-red-800',
  'bg-green-100 text-green-800',
  'bg-yellow-100 text-yellow-800',
  'bg-blue-100 text-blue-800',
  'bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800',
  'bg-gray-100 text-gray-800',
  'bg-indigo-100 text-indigo-800',
  'bg-teal-100 text-teal-800',
  'bg-lime-100 text-lime-800',
];

const Categories = ({
  tournament,
  categories,
}: {
  tournament: Tournament;
  categories: Category[];
}) => {
  const revalidator = useRevalidator();

  const addCategory = async (categoryId: string) => {
    await clubApi.addTournamentCategory(tournament.club.id, tournament.id, categoryId);
    revalidator.revalidate();
  };

  const removeCategory = async (categoryId: string) => {
    await clubApi.removeTournamentCategory(tournament.club.id, tournament.id, categoryId);
    revalidator.revalidate();
  };

  const tagsColorMap = useMemo(() => {
    const allTags = categories.flatMap((category) => category.tagList) || [];
    const uniqueTags = [...new Set(allTags)];
    const colorMap: Record<string, string> = {};
    for (const tag of uniqueTags) {
      colorMap[tag] = TAGS_COLORS[uniqueTags.indexOf(tag) % TAGS_COLORS.length];
    }
    return colorMap;
  }, [categories]);

  if (!tournament || !tournament.categories) {
    return <div>Torneo no encontrado</div>;
  }

  const checkedCategories = tournament.categories.map((category) => category.id);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1 mb-4">
        <h2 className="text-lg font-medium">Categorías</h2>
        <h3 className="text-sm text-neutral-500">
          Selecciona las categorías que quieres que participen en el torneo
        </h3>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        {categories.map((category) => {
          const isChecked = checkedCategories.includes(category.id);
          return (
            <div
              key={category.id}
              className={`flex flex-col items-center justify-center gap-4 rounded-2xl border  ${
                isChecked ? 'border-neutral-300 bg-neutral-50' : 'border-neutral-100 bg-white'
              } cursor-pointer p-4 transition-colors hover:bg-neutral-200`}
              onClick={() => {
                if (checkedCategories.includes(category.id)) {
                  removeCategory(category.id);
                } else {
                  addCategory(category.id);
                }
              }}
            >
              <div className="flex w-full items-center gap-2">
                {checkedCategories.includes(category.id) ? (
                  <CircleCheck className="h-4 w-4 stroke-primary stroke-2" />
                ) : (
                  <Circle className="h-4 w-4 stroke-1" />
                )}
                <span className="text-sm font-medium">{category.name}</span>
              </div>
              <div className="w-full text-xs text-neutral-500">{category.description}</div>
              <div className="flex w-full flex-wrap items-start gap-2 text-sm text-neutral-500">
                {category.tagList.map((tag) => {
                  return (
                    <div
                      key={tag}
                      className={`capitalize rounded-full px-2 py-1 text-xs ${tagsColorMap[tag]}`}
                    >
                      {tag}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Categories;
